import Fuse from 'fuse.js'
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  ExternalLink,
  FileQuestion,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Tags,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Tabs } from '@/components/admin/coaching/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getIconUrl } from '@/lib/icons'
import { slugify } from '@/lib/slug'
import type { Company, CompanyDetail } from './hooks/use-companies'
import { useCompanies } from './hooks/use-companies'
import { useCompanyTags } from './hooks/use-company-tags'
import { TagManagementModal } from './tag-management-modal'

function getDifficultyColor(difficulty: string | null | undefined): string {
  switch (difficulty?.toUpperCase()) {
    case 'EASY':
      return 'text-green-500 bg-green-500/10'
    case 'MEDIUM':
      return 'text-yellow-500 bg-yellow-500/10'
    case 'HARD':
      return 'text-red-500 bg-red-500/10'
    default:
      return 'text-muted-foreground bg-muted'
  }
}

function getTagColor(slug: string): string {
  switch (slug) {
    case 'big-tech':
      return 'text-blue-600 bg-blue-500/10'
    case 'ai-ml':
      return 'text-purple-600 bg-purple-500/10'
    case 'hedge-fund':
      return 'text-green-600 bg-green-500/10'
    case 'unicorn':
      return 'text-pink-600 bg-pink-500/10'
    case 'mid-size':
      return 'text-orange-600 bg-orange-500/10'
    case 'thin-tech':
      return 'text-cyan-600 bg-cyan-500/10'
    case 'fintech':
      return 'text-emerald-600 bg-emerald-500/10'
    case 'startup':
      return 'text-amber-600 bg-amber-500/10'
    default:
      return 'text-muted-foreground bg-muted'
  }
}

function getTagBarColor(slug: string): string {
  switch (slug) {
    case 'big-tech':
      return 'bg-blue-500'
    case 'ai-ml':
      return 'bg-purple-500'
    case 'hedge-fund':
      return 'bg-green-500'
    case 'unicorn':
      return 'bg-pink-500'
    case 'mid-size':
      return 'bg-orange-500'
    case 'fintech':
      return 'bg-emerald-500'
    case 'assessment-platform':
      return 'bg-sky-500'
    case 'government':
      return 'bg-red-500'
    default:
      return 'bg-muted-foreground'
  }
}

function CompanyCard({
  company,
  onClick,
}: {
  company: Company
  onClick: (company: Company) => void
}) {
  const imgSrc = getIconUrl(company.imgUrl)
  return (
    <div
      onClick={() => onClick(company)}
      className="bg-card border-border hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors"
    >
      {imgSrc !== null ? (
        <img
          src={imgSrc}
          alt={company.name}
          className="h-10 w-10 rounded-lg object-contain"
        />
      ) : (
        <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-lg">
          <Building2 size={20} className="text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        {company.parent && (
          <p className="text-muted-foreground truncate text-[11px] leading-tight">
            {company.parent.name}
          </p>
        )}
        <p className="text-foreground truncate text-base font-medium">
          {company.name}
        </p>
      </div>
      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
    </div>
  )
}

export function CompaniesList() {
  const {
    companies,
    loading,
    error,
    refresh,
    addCompany,
    updateCompany,

    getCompany,
    deleteCompany,
  } = useCompanies()
  const {
    tags: companyTags,
    addTag,
    updateTag,
    deleteTag,
    reorderTags,
  } = useCompanyTags()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showTagManagement, setShowTagManagement] = useState(false)
  const [activeTag, setActiveTag] = useState<string>('all')

  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<CompanyDetail | null>(
    null,
  )
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editCompany, setEditCompany] = useState<{
    id: string
    name: string
    slug: string
    imgUrl: string
    aliases: string[]
    parentId: string | null
    tagIds: string[]
  } | null>(null)
  const [parentSearchQuery, setParentSearchQuery] = useState('')
  const [showParentDropdown, setShowParentDropdown] = useState(false)
  const [newAliasInput, setNewAliasInput] = useState('')
  const [newCompany, setNewCompany] = useState({
    name: '',
    slug: '',
    imgUrl: '',
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  // Fuzzy search with fuse.js
  const companiesWithTagNames = useMemo(
    () =>
      companies.map((c) => ({
        ...c,
        tagNames: (c.tags || []).map((t) => t.name),
      })),
    [companies],
  )

  const fuse = useMemo(
    () =>
      new Fuse(companiesWithTagNames, {
        keys: [
          { name: 'name', weight: 0.45 },
          { name: 'slug', weight: 0.15 },
          { name: 'aliases', weight: 0.2 },
          { name: 'tagNames', weight: 0.2 },
        ],
        threshold: 0.4,
        minMatchCharLength: 2,
        includeScore: true,
        shouldSort: true,
      }),
    [companiesWithTagNames],
  )

  // First apply search
  const searchedCompanies = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return companies
    return fuse.search(searchQuery).map((r) => r.item)
  }, [companies, searchQuery, fuse])

  // Then apply tag filter
  const filteredCompanies = useMemo(() => {
    if (activeTag === 'all') return searchedCompanies
    return searchedCompanies.filter((c) =>
      c.tags?.some((t) => t.id === activeTag),
    )
  }, [searchedCompanies, activeTag])

  // Build tabs array with counts from searched (not tag-filtered) companies
  const tagTabs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const c of searchedCompanies) {
      for (const t of c.tags || []) {
        counts.set(t.id, (counts.get(t.id) || 0) + 1)
      }
    }

    return [
      { id: 'all', label: 'All Companies', count: searchedCompanies.length },
      ...companyTags
        .filter((t) => (counts.get(t.id) || 0) > 0)
        .map((t) => ({
          id: t.id,
          label: t.name,
          count: counts.get(t.id) || 0,
        })),
    ]
  }, [searchedCompanies, companyTags])

  const handleCompanyClick = async (company: Company) => {
    setIsLoadingDetail(true)
    try {
      const detail = await getCompany(company.id)
      setSelectedCompany(detail)
      setShowDetailModal(true)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to load company details',
      )
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!companyToDelete) return

    setIsSubmitting(true)
    try {
      await deleteCompany(companyToDelete.id)
      toast.success(`"${companyToDelete.name}" deleted successfully`)
      setShowDeleteConfirm(false)
      setCompanyToDelete(null)
      // If we're viewing the deleted company, close the detail modal
      if (selectedCompany?.id === companyToDelete.id) {
        setShowDetailModal(false)
        setSelectedCompany(null)
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to delete company',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddCompany = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newCompany.name.trim()) {
      toast.error('Company name is required')
      return
    }

    setIsSubmitting(true)
    try {
      await addCompany({
        name: newCompany.name.trim(),
        slug: newCompany.slug.trim() || undefined,
        imgUrl: newCompany.imgUrl.trim() || undefined,
      })
      toast.success('Company added successfully')
      setNewCompany({ name: '', slug: '', imgUrl: '' })
      setShowAddModal(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add company')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading companies...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-foreground text-xl font-bold">
              Browse by Company
            </h1>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search
                  size={16}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search companies..."
                  className="pl-9"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTagManagement(true)}
              >
                <Tags size={16} className="mr-1.5" />
                Tags
              </Button>

              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus size={16} className="mr-1.5" />
                Add
              </Button>
            </div>
          </div>
          {companyTags.length > 0 && (
            <div className="mt-3">
              <Tabs
                tabs={tagTabs}
                activeTab={activeTag}
                onTabChange={setActiveTag}
              />
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6">
        {error ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Building2 size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              Unable to load companies
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw size={18} className="mr-2" />
              Retry
            </Button>
          </div>
        ) : companies.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Building2 size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              No companies yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first company to get started.
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus size={18} className="mr-2" />
              Add Company
            </Button>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="py-12 text-center">
            <Search size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              No companies found matching "{searchQuery}"
            </p>
          </div>
        ) : activeTag === 'all' ? (
          <div className="space-y-8">
            {companyTags.map((tag) => {
              const tagCompanies = filteredCompanies.filter((c) =>
                c.tags?.some((t) => t.id === tag.id),
              )
              if (tagCompanies.length === 0) return null
              return (
                <section key={tag.id}>
                  <div className="mb-3 flex items-center gap-2">
                    <div
                      className={`h-5 w-1 rounded-full ${getTagBarColor(tag.slug)}`}
                    />
                    <h3 className="text-foreground font-semibold">
                      {tag.name}
                    </h3>
                    <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
                      {tagCompanies.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {tagCompanies.map((c) => (
                      <CompanyCard
                        key={`${tag.slug}-${c.id}`}
                        company={c}
                        onClick={handleCompanyClick}
                      />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredCompanies.map((c) => (
              <CompanyCard
                key={c.id}
                company={c}
                onClick={handleCompanyClick}
              />
            ))}
          </div>
        )}
      </main>

      {/* Company Detail Modal */}
      {showDetailModal && selectedCompany && (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border shadow-lg">
            {/* Header */}
            <div className="border-border flex items-center justify-between border-b p-6">
              <div className="flex items-center gap-4">
                {(() => {
                  const imgSrc = getIconUrl(selectedCompany.imgUrl)
                  return imgSrc !== null ? (
                    <img
                      src={imgSrc}
                      alt={selectedCompany.name}
                      className="h-12 w-12 rounded object-contain"
                    />
                  ) : (
                    <div className="bg-muted flex h-12 w-12 items-center justify-center rounded">
                      <Building2 size={24} className="text-muted-foreground" />
                    </div>
                  )
                })()}
                <div>
                  <h2 className="text-foreground text-xl font-semibold">
                    {selectedCompany.name}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {selectedCompany.slug}
                  </p>
                  {selectedCompany.parent && (
                    <p className="text-muted-foreground text-xs">
                      subsidiary of {selectedCompany.parent.name}
                    </p>
                  )}
                  {selectedCompany.aliases &&
                    selectedCompany.aliases.length > 0 && (
                      <p className="text-muted-foreground text-xs">
                        aka {selectedCompany.aliases.join(', ')}
                      </p>
                    )}
                  {selectedCompany.tags && selectedCompany.tags.length > 0 && (
                    <div className="mt-1 flex gap-1">
                      {selectedCompany.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${getTagColor(tag.slug)}`}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditCompany({
                      id: selectedCompany.id,
                      name: selectedCompany.name,
                      slug: selectedCompany.slug,
                      imgUrl: selectedCompany.imgUrl || '',
                      aliases: selectedCompany.aliases || [],
                      parentId: selectedCompany.parentId || null,
                      tagIds: (selectedCompany.tags || []).map((t) => t.id),
                    })
                    setNewAliasInput('')
                    setParentSearchQuery('')
                    setShowEditModal(true)
                  }}
                  className="text-muted-foreground hover:text-foreground rounded p-2 transition-colors"
                  title="Edit company"
                >
                  <Pencil size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCompanyToDelete(selectedCompany)
                    setShowDeleteConfirm(true)
                  }}
                  className="text-muted-foreground hover:text-destructive rounded p-2 transition-colors"
                  title="Delete company"
                >
                  <Trash2 size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false)
                    setSelectedCompany(null)
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Subsidiaries */}
              {selectedCompany.children &&
                selectedCompany.children.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-foreground mb-2 text-sm font-medium">
                      Subsidiaries
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedCompany.children.map((child) => {
                        const childImgSrc = getIconUrl(child.imgUrl)
                        return (
                          <button
                            key={child.id}
                            type="button"
                            onClick={async () => {
                              setIsLoadingDetail(true)
                              try {
                                const detail = await getCompany(child.id)
                                setSelectedCompany(detail)
                              } catch (_err) {
                                toast.error('Failed to load subsidiary')
                              } finally {
                                setIsLoadingDetail(false)
                              }
                            }}
                            className="bg-muted/50 hover:bg-muted text-foreground flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors"
                          >
                            {childImgSrc !== null ? (
                              <img
                                src={childImgSrc}
                                alt={child.name}
                                className="h-5 w-5 rounded object-contain"
                              />
                            ) : (
                              <Building2
                                size={14}
                                className="text-muted-foreground"
                              />
                            )}
                            {child.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-foreground text-lg font-medium">
                  Related Problems
                </h3>
                <span className="text-muted-foreground text-sm">
                  {selectedCompany.problemCount} total
                </span>
              </div>

              {selectedCompany.problems.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="bg-muted/50 mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
                    <FileQuestion size={32} className="text-muted-foreground" />
                  </div>
                  <h4 className="text-foreground mb-2 font-medium">
                    No problems associated
                  </h4>
                  <p className="text-muted-foreground mx-auto max-w-sm text-sm">
                    This company doesn't have any problems linked to it yet.
                    Problems will appear here once they are associated with this
                    company.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedCompany.problems.map((problem) => (
                    <div
                      key={problem.id}
                      className="bg-muted/30 hover:bg-muted/50 flex items-center justify-between rounded-lg p-4 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground font-mono text-sm">
                            #{problem.externalId || '—'}
                          </span>
                          <h4 className="text-foreground font-medium">
                            {problem.title}
                          </h4>
                          {problem.premiumRequired && (
                            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-xs font-medium text-amber-500">
                              Premium
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {problem.difficulty && (
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}
                            >
                              {problem.difficulty}
                            </span>
                          )}
                          {problem.acceptanceRate !== null &&
                            problem.acceptanceRate !== undefined && (
                              <span className="text-muted-foreground text-xs">
                                {problem.acceptanceRate.toFixed(1)}% acceptance
                              </span>
                            )}
                          {problem.topics && problem.topics.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {problem.topics.slice(0, 3).map((topic) => (
                                <span
                                  key={topic.slug}
                                  className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs"
                                >
                                  {topic.name}
                                </span>
                              ))}
                              {problem.topics.length > 3 && (
                                <span className="text-muted-foreground text-xs">
                                  +{problem.topics.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {problem.url && (
                        <a
                          href={problem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground ml-2 rounded p-2 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Detail Overlay */}
      {isLoadingDetail && (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="text-muted-foreground flex items-center gap-3">
            <RefreshCw size={20} className="animate-spin" />
            <span>Loading company details...</span>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && companyToDelete && (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-destructive/10 flex h-10 w-10 items-center justify-center rounded-full">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-foreground text-lg font-semibold">
                  Delete Company
                </h2>
                <p className="text-muted-foreground text-sm">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-foreground mb-6">
              Are you sure you want to delete{' '}
              <strong>{companyToDelete.name}</strong>? This will remove the
              company but won't affect any associated problems.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setCompanyToDelete(null)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Company'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddModal && (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-foreground text-lg font-semibold">
                Add Company
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false)
                  setNewCompany({ name: '', slug: '', imgUrl: '' })
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddCompany} className="space-y-4">
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => {
                    setNewCompany({
                      ...newCompany,
                      name: e.target.value,
                      slug: newCompany.slug || slugify(e.target.value),
                    })
                  }}
                  placeholder="e.g., Google"
                  required
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Slug
                </label>
                <Input
                  type="text"
                  value={newCompany.slug}
                  onChange={(e) =>
                    setNewCompany({ ...newCompany, slug: e.target.value })
                  }
                  placeholder="e.g., google"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Image
                </label>
                <Input
                  type="text"
                  value={newCompany.imgUrl}
                  onChange={(e) =>
                    setNewCompany({ ...newCompany, imgUrl: e.target.value })
                  }
                  placeholder="e.g., google.png"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowAddModal(false)
                    setNewCompany({ name: '', slug: '', imgUrl: '' })
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Adding...' : 'Add Company'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditModal && editCompany && (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-foreground text-lg font-semibold">
                Edit Company
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false)
                  setEditCompany(null)
                  setNewAliasInput('')
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!editCompany.name.trim()) {
                  toast.error('Company name is required')
                  return
                }
                setIsSubmitting(true)
                try {
                  await updateCompany(editCompany.id, {
                    name: editCompany.name.trim(),
                    slug: editCompany.slug.trim() || slugify(editCompany.name),
                    imgUrl: editCompany.imgUrl.trim() || null,
                    aliases: editCompany.aliases,
                    parentId: editCompany.parentId,
                    tagIds: editCompany.tagIds,
                  })
                  toast.success('Company updated successfully')
                  setShowEditModal(false)
                  setEditCompany(null)
                  setNewAliasInput('')
                  // Refresh detail modal if open
                  if (selectedCompany?.id === editCompany.id) {
                    const detail = await getCompany(editCompany.id)
                    setSelectedCompany(detail)
                  }
                } catch (err) {
                  toast.error(
                    err instanceof Error
                      ? err.message
                      : 'Failed to update company',
                  )
                } finally {
                  setIsSubmitting(false)
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={editCompany.name}
                  onChange={(e) =>
                    setEditCompany({ ...editCompany, name: e.target.value })
                  }
                  placeholder="e.g., Google"
                  required
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Slug
                </label>
                <Input
                  type="text"
                  value={editCompany.slug}
                  onChange={(e) =>
                    setEditCompany({ ...editCompany, slug: e.target.value })
                  }
                  placeholder="e.g., google"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Image
                </label>
                <Input
                  type="text"
                  value={editCompany.imgUrl}
                  onChange={(e) =>
                    setEditCompany({ ...editCompany, imgUrl: e.target.value })
                  }
                  placeholder="e.g., google.png"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Aliases
                </label>
                {editCompany.aliases.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {editCompany.aliases.map((alias) => (
                      <span
                        key={alias}
                        className="bg-muted text-foreground inline-flex items-center gap-1 rounded px-2 py-0.5 text-sm"
                      >
                        {alias}
                        <button
                          type="button"
                          onClick={() =>
                            setEditCompany({
                              ...editCompany,
                              aliases: editCompany.aliases.filter(
                                (a) => a !== alias,
                              ),
                            })
                          }
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newAliasInput}
                    onChange={(e) => setNewAliasInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        const alias = newAliasInput.trim()
                        if (alias && !editCompany.aliases.includes(alias)) {
                          setEditCompany({
                            ...editCompany,
                            aliases: [...editCompany.aliases, alias],
                          })
                          setNewAliasInput('')
                        }
                      }
                    }}
                    placeholder="e.g., Facebook"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const alias = newAliasInput.trim()
                      if (alias && !editCompany.aliases.includes(alias)) {
                        setEditCompany({
                          ...editCompany,
                          aliases: [...editCompany.aliases, alias],
                        })
                        setNewAliasInput('')
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-sm font-medium">
                  Parent Company
                </label>
                <div className="relative">
                  {editCompany.parentId ? (
                    <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2 text-sm">
                      <span className="text-foreground flex-1">
                        {companies.find((c) => c.id === editCompany.parentId)
                          ?.name || 'Unknown'}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setEditCompany({ ...editCompany, parentId: null })
                        }
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Input
                        type="text"
                        value={parentSearchQuery}
                        onChange={(e) => {
                          setParentSearchQuery(e.target.value)
                          setShowParentDropdown(true)
                        }}
                        onFocus={() => setShowParentDropdown(true)}
                        placeholder="Search for parent company..."
                      />
                      {showParentDropdown && parentSearchQuery.length >= 1 && (
                        <div className="border-border bg-card absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded-md border shadow-lg">
                          {companies
                            .filter(
                              (c) =>
                                c.id !== editCompany.id &&
                                c.name
                                  .toLowerCase()
                                  .includes(parentSearchQuery.toLowerCase()),
                            )
                            .slice(0, 8)
                            .map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                  setEditCompany({
                                    ...editCompany,
                                    parentId: c.id,
                                  })
                                  setParentSearchQuery('')
                                  setShowParentDropdown(false)
                                }}
                                className="text-foreground hover:bg-muted w-full px-3 py-2 text-left text-sm"
                              >
                                {c.name}
                              </button>
                            ))}
                          {companies.filter(
                            (c) =>
                              c.id !== editCompany.id &&
                              c.name
                                .toLowerCase()
                                .includes(parentSearchQuery.toLowerCase()),
                          ).length === 0 && (
                            <p className="text-muted-foreground px-3 py-2 text-sm">
                              No companies found
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {companyTags.length > 0 && (
                <div>
                  <label className="text-muted-foreground mb-1 block text-sm font-medium">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {companyTags.map((tag) => {
                      const isSelected = editCompany.tagIds.includes(tag.id)
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            setEditCompany({
                              ...editCompany,
                              tagIds: isSelected
                                ? editCompany.tagIds.filter(
                                    (id) => id !== tag.id,
                                  )
                                : [...editCompany.tagIds, tag.id],
                            })
                          }}
                          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                            isSelected
                              ? getTagColor(tag.slug)
                              : 'bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {tag.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditCompany(null)
                    setNewAliasInput('')
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Management Modal */}
      {showTagManagement && (
        <TagManagementModal
          tags={companyTags}
          onAdd={addTag}
          onUpdate={updateTag}
          onDelete={deleteTag}
          onReorder={reorderTags}
          onClose={() => setShowTagManagement(false)}
        />
      )}
    </div>
  )
}
